import { useState, useEffect } from "react";
import { ChevronRight, Triangle, Square } from "lucide-react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import Thumbnail from "../../common/Thumbnail";
import ScreenshotViewer from "../../common/ScreenshotViewer";

const getColumnValue = (item, heading, screenshot, onThumbnailClick) => {
  const value = item[heading.key];
  const itemType = heading.itemType || heading.valueType;

  switch (itemType) {
    case "thumbnail":
      return <img src={value} alt="thumbnail" className="w-8 h-8" />;
    case "url":
      const url =
        typeof value === "object" && value !== null ? value.url : value;
      if (!url || typeof url !== "string") return null;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand hover:underline whitespace-nowrap md:whitespace-normal md:break-all"
          title={url}
        >
          {url}
        </a>
      );
    case "node":
      if (!value || typeof value !== "object") {
        return null;
      }
      return (
        <div className="flex items-start space-x-3">
          {value.boundingRect && screenshot && (
            <div className="flex-shrink-0">
              <Thumbnail
                screenshot={screenshot}
                rect={value.boundingRect}
                onClick={() => onThumbnailClick(value.boundingRect)}
              />
            </div>
          )}
          {value.snippet && (
            <span className="font-mono text-xs self-start whitespace-nowrap md:whitespace-normal md:break-all">
              {value.snippet}
            </span>
          )}
        </div>
      );
    case "source-location":
      if (value && value.url) {
        const line = value.line ? `:${value.line}` : "";
        const column = value.column ? `:${value.column}` : "";
        return (
          <a
            href={value.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:underline whitespace-nowrap md:whitespace-normal md:break-all"
            title={`${value.url}${line}${column}`}
          >
            {`${value.url.split("/").pop()}${line}${column}`}
          </a>
        );
      }
      return null;
    case "bytes":
      return formatBytes(value);
    case "ms":
      return formatTime(value);
    default:
      if (typeof value === "object" && value !== null) {
        return null;
      }
      return value;
  }
};

const formatBytes = (bytes) => {
  if (typeof bytes !== "number" || isNaN(bytes)) return "N/A";
  return `${(bytes / 1024).toFixed(2)} KB`;
};

const formatTime = (ms) => {
  if (typeof ms !== "number" || isNaN(ms)) return "N/A";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

import IssueTitle from "../../common/IssueTitle";
import StickyIssueHeader from "../../common/StickyIssueHeader";
import { issueCardOpenShellClass, issueCardBodyClass, issueCardCollapsedClass } from "../../../utils/stickyIssueHeader";

const BestPracticeIssueDetails = ({ knownIssues, screenshot }) => {
  const [openIssues, setOpenIssues] = useState({});
  const [selectedRect, setSelectedRect] = useState(null);

  // Only keep Best Practice issues
  const bestPracticeIssues = Object.entries(knownIssues).filter(
    ([, issue]) => issue.category === "best-practices"
  );

  useEffect(() => {
    if (selectedRect) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedRect]);

  const handleThumbnailClick = (rect) => {
    setSelectedRect(rect);
  };

  const handleCloseViewer = () => {
    setSelectedRect(null);
  };

  const toggleIssue = (key) => {
    setOpenIssues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!bestPracticeIssues || bestPracticeIssues.length === 0) {
    return (
      <div className="mt-6 p-4 bg-green-100 border border-green-200 rounded-lg">
        <p className="text-green-800 font-semibold">
          No major Best Practice issues detected. Great job!
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="space-y-3">
        {bestPracticeIssues.map(([key, issue]) => {
          const isExpandable =
            (issue.items && issue.items.length > 0) || issue.description;
          const headings = issue.headings || [
            { key: "node", itemType: "node", label: "Failing Elements" },
          ];
          // Icon logic
          let icon = null;
          if (issue.score < 0.5) {
            icon = (
              <Triangle
                size={16}
                className="text-red-500 mr-3 mt-1 flex-shrink-0"
              />
            );
          } else if (issue.score < 0.9) {
            icon = (
              <Square
                size={16}
                className="text-orange-500 mr-3 mt-1 flex-shrink-0"
              />
            );
          }
          const isOpen = openIssues[key] && isExpandable;

          return (
            <div
              key={key}
              data-issue-card
              className={isOpen ? issueCardOpenShellClass : issueCardCollapsedClass}
            >
              <StickyIssueHeader
                active={isOpen}
                className={`flex items-start p-4 ${
                  isOpen
                    ? isExpandable
                      ? "cursor-pointer"
                      : ""
                    : isExpandable
                      ? "cursor-pointer"
                      : ""
                }`}
                onClick={() => isExpandable && toggleIssue(key)}
              >
                {icon}
                <div className="flex-grow flex justify-between items-center min-w-0 gap-x-4">
                  <IssueTitle issueKey={key} issue={issue} />
                  {issue.display && (
                    <span className="text-sm text-muted-foreground font-medium text-right flex-shrink-0 w-1/3 leading-tight">
                      {issue.display}
                    </span>
                  )}
                </div>
                {isExpandable && (
                  <ChevronRight
                    size={20}
                    className={`ml-2 text-muted-foreground flex-shrink-0 transition-transform mt-1 ${
                      openIssues[key] ? "rotate-90" : ""
                    }`}
                  />
                )}
              </StickyIssueHeader>
              {isOpen && (
                <div className={issueCardBodyClass}>
                  {issue.description &&
                    (!issue.items || issue.items.length === 0) && (
                      <div
                        className="prose prose-sm max-w-none text-foreground diagnostic-description"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            marked.parse(issue.description)
                          ),
                        }}
                      ></div>
                    )}
                  {issue.items && issue.items.length > 0 && (
                    <>
                      {issue.description && (
                        <div
                          className="prose prose-sm max-w-none text-foreground mb-4 diagnostic-description"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              marked.parse(issue.description)
                            ),
                          }}
                        ></div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-muted-foreground">
                              {headings.map((heading) => (
                                <th
                                  key={heading.key}
                                  className={`py-2 px-4 font-semibold ${
                                    heading.itemType === "bytes" ||
                                    heading.itemType === "ms"
                                      ? "text-right"
                                      : ""
                                  }`}
                                >
                                  {heading.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {issue.items.map((item, index) => (
                              <tr key={index}>
                                {headings.map((heading) => (
                                  <td
                                    key={`${index}-${heading.key}`}
                                    className={`py-2 px-4 align-top ${
                                      heading.itemType === "bytes" ||
                                      heading.itemType === "ms"
                                        ? "text-right"
                                        : ""
                                    }`}
                                  >
                                    {getColumnValue(
                                      item,
                                      heading,
                                      screenshot,
                                      handleThumbnailClick
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {selectedRect && (
        <ScreenshotViewer
          screenshot={screenshot}
          rect={selectedRect}
          onClose={handleCloseViewer}
        />
      )}
    </div>
  );
};

export default BestPracticeIssueDetails;
